import React, { useMemo } from 'react';
import type { FormField } from '@troia-v3/types';

import { Button, Modal, ModalContent, ModalFooter, ModalHeader } from '@/shared/components/ui';

import { FormFieldPreview } from './FormFieldPreview';

export interface FormPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Nome do modelo — vai no header. */
  name: string;
  description?: string;
  fields: FormField[];
  'data-testid'?: string;
}

/**
 * FormPreviewModal — pré-visualização INTERNA de um modelo (fields renderizados
 * na ordem com o mesmo `FormFieldPreview` do canvas do builder).
 *
 * Existe porque o preview de um modelo `type: 'checklist'` NÃO pode usar a
 * rota pública `/f/:slug`: o backend fecha o formulário público para checklists
 * (404 por design — checklist é preenchido por um responsável autenticado em
 * `/checklists/:id/fill`, nunca por link público). Este modal não cria
 * instância nem chama submit — é leitura pura.
 */
export const FormPreviewModal: React.FC<FormPreviewModalProps> = ({
  isOpen,
  onClose,
  name,
  description,
  fields,
  'data-testid': dataTestId = 'form-preview-modal',
}) => {
  // `order` é a fonte da verdade da sequência (o builder reordena in-place, mas
  // um modelo salvo pode chegar com os campos fora de ordem no array).
  const orderedFields = useMemo(
    () => [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [fields],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="3xl">
      <div data-testid={dataTestId}>
        <ModalHeader title={name || 'Modelo sem título'} onClose={onClose} />
        <ModalContent>
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <div
              className="flex items-start gap-2 rounded-lg px-3 py-2 mb-4 text-xs"
              style={{ backgroundColor: '#f5f3ff', color: '#6d28d9' }}
              data-testid="form-preview-badge"
            >
              Pré-visualização — os campos ficam ativos no preenchimento real.
            </div>

            {description && (
              <p className="text-sm mb-4" style={{ color: 'var(--theme-muted-text)' }}>
                {description}
              </p>
            )}

            {orderedFields.length === 0 ? (
              <p className="text-sm py-8 text-center" style={{ color: 'var(--theme-muted-text)' }}>
                Nenhum campo adicionado ainda.
              </p>
            ) : (
              <div className="space-y-5">
                {orderedFields.map((field) => (
                  <div key={field.id} data-testid={`form-preview-field-${field.id}`}>
                    <FormFieldPreview field={field} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} data-testid="form-preview-close">
            Fechar
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

FormPreviewModal.displayName = 'FormPreviewModal';
